/**
 * Add Vehicle Wizard Component
 * ============================
 * Multi-step wizard for adding a vehicle to the fleet.
 * Step 1: Enter VIN → auto-decode
 * Step 2: Fleet-specific details (unit #, department, driver)
 * Step 3: Confirm and create
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Search,
  Truck,
  AlertCircle,
  Building2,
  User,
  Hash,
  FileText,
  Calendar,
  Gauge,
} from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { fleetApi } from '../../services/fleetApi';

// Step indicator component
const StepIndicator = ({ steps, currentStep }) => {
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                index < currentStep
                  ? 'bg-accent text-white'
                  : index === currentStep
                  ? 'bg-accent text-white'
                  : 'bg-surface-muted text-ink-muted'
              }`}
            >
              {index < currentStep ? (
                <Check className="w-5 h-5" />
              ) : (
                index + 1
              )}
            </div>
            <span
              className={`ml-3 text-sm font-medium ${
                index <= currentStep ? 'text-ink-soft' : 'text-ink-muted'
              }`}
            >
              {step.title}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-16 h-0.5 mx-4 ${
                index < currentStep ? 'bg-accent' : 'bg-surface-muted'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Step 1: VIN Entry
const Step1VIN = ({ vin, setVin, vinData, setVinData, isDecoding, onDecode }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (vin.length === 17) {
      onDecode();
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Search className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-xl font-semibold text-ink-soft">Enter Vehicle VIN</h2>
        <p className="mt-2 text-ink-muted">
          We'll automatically decode the VIN to get vehicle details
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-6">
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Vehicle Identification Number (VIN)
          </label>
          <div className="relative">
            <input
              type="text"
              value={vin}
              onChange={(e) => {
                setVin(e.target.value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, ''));
                setVinData(null);
              }}
              maxLength={17}
              placeholder="Enter 17-character VIN"
              className="w-full px-4 py-3 border border-border-default rounded-lg text-lg font-mono tracking-wide focus:ring-2 focus:ring-accent focus:border-accent"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-ink-muted">
              {vin.length}/17
            </span>
          </div>
          {vin.length > 0 && vin.length < 17 && (
            <p className="mt-2 text-sm text-amber-600">
              VIN must be exactly 17 characters
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={vin.length !== 17 || isDecoding}
          className="w-full py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isDecoding ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Decoding VIN...
            </>
          ) : (
            <>
              Decode VIN
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </button>
      </form>

      {/* Decoded Vehicle Info */}
      {vinData && (
        <div className="mt-8 p-6 bg-success-bg border border-success rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Check className="w-6 h-6 text-success" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-success">
                Vehicle Identified
              </h3>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-success">Year:</span>
                  <span className="ml-2 text-ink-soft font-medium">{vinData.year}</span>
                </div>
                <div>
                  <span className="text-success">Make:</span>
                  <span className="ml-2 text-ink-soft font-medium">{vinData.make}</span>
                </div>
                <div>
                  <span className="text-success">Model:</span>
                  <span className="ml-2 text-ink-soft font-medium">{vinData.model}</span>
                </div>
                <div>
                  <span className="text-success">Trim:</span>
                  <span className="ml-2 text-ink-soft font-medium">{vinData.trim || 'N/A'}</span>
                </div>
                {vinData.engine && (
                  <div className="col-span-2">
                    <span className="text-success">Engine:</span>
                    <span className="ml-2 text-ink-soft font-medium">{vinData.engine}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Step 2: Fleet Details
const Step2Details = ({ formData, setFormData, departments, drivers }) => {
  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-accent" />
        </div>
        <h2 className="text-xl font-semibold text-ink-soft">Fleet Details</h2>
        <p className="mt-2 text-ink-muted">
          Add fleet-specific information for this vehicle
        </p>
      </div>

      <div className="space-y-6">
        {/* Unit Number */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            <Hash className="w-4 h-4 inline mr-2" />
            Unit Number
          </label>
          <input
            type="text"
            value={formData.unit_number}
            onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
            placeholder="e.g., PW-12, TRUCK-001"
            className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          />
          <p className="mt-1 text-sm text-ink-muted">
            Your internal fleet identifier for this vehicle
          </p>
        </div>

        {/* License Plate */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-2">
              License Plate
            </label>
            <input
              type="text"
              value={formData.license_plate}
              onChange={(e) => setFormData({ ...formData, license_plate: e.target.value.toUpperCase() })}
              placeholder="ABC 123"
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-2">
              Province/State
            </label>
            <select
              value={formData.license_plate_state_province}
              onChange={(e) => setFormData({ ...formData, license_plate_state_province: e.target.value })}
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
            >
              <option value="">Select...</option>
              <option value="ON">Ontario</option>
              <option value="QC">Quebec</option>
              <option value="BC">British Columbia</option>
              <option value="AB">Alberta</option>
              <option value="MB">Manitoba</option>
              <option value="SK">Saskatchewan</option>
              <option value="NS">Nova Scotia</option>
              <option value="NB">New Brunswick</option>
              <option value="NL">Newfoundland</option>
              <option value="PE">Prince Edward Island</option>
              <option value="NT">Northwest Territories</option>
              <option value="YT">Yukon</option>
              <option value="NU">Nunavut</option>
            </select>
          </div>
        </div>

        {/* Department */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            <Building2 className="w-4 h-4 inline mr-2" />
            Department
          </label>
          <select
            value={formData.department_id || ''}
            onChange={(e) => setFormData({ ...formData, department_id: e.target.value || null })}
            className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          >
            <option value="">No department</option>
            {departments?.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name} {dept.code && `(${dept.code})`}
              </option>
            ))}
          </select>
        </div>

        {/* Assigned Driver */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Assigned Driver
          </label>
          <select
            value={formData.assigned_driver_id || ''}
            onChange={(e) => setFormData({ ...formData, assigned_driver_id: e.target.value || null })}
            className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          >
            <option value="">Unassigned</option>
            {drivers?.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driver.first_name} {driver.last_name}
                {driver.employee_id && ` (${driver.employee_id})`}
              </option>
            ))}
          </select>
        </div>

        {/* Odometer */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-2">
              <Gauge className="w-4 h-4 inline mr-2" />
              Current Odometer
            </label>
            <input
              type="number"
              value={formData.current_odometer || ''}
              onChange={(e) => setFormData({ ...formData, current_odometer: parseInt(e.target.value) || null })}
              placeholder="0"
              min="0"
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-2">
              Unit
            </label>
            <select
              value={formData.odometer_unit}
              onChange={(e) => setFormData({ ...formData, odometer_unit: e.target.value })}
              className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
            >
              <option value="km">Kilometers</option>
              <option value="mi">Miles</option>
            </select>
          </div>
        </div>

        {/* Acquisition Date */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            <Calendar className="w-4 h-4 inline mr-2" />
            Acquisition Date
          </label>
          <input
            type="date"
            value={formData.acquisition_date || ''}
            onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value || null })}
            className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          />
        </div>

        {/* Fuel Type */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Fuel Type
          </label>
          <select
            value={formData.fuel_type || ''}
            onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value || null })}
            className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          >
            <option value="">Select...</option>
            <option value="gasoline">Gasoline</option>
            <option value="diesel">Diesel</option>
            <option value="electric">Electric</option>
            <option value="hybrid">Hybrid</option>
            <option value="plugin_hybrid">Plug-in Hybrid</option>
            <option value="propane">Propane</option>
            <option value="natural_gas">Natural Gas</option>
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Notes
          </label>
          <textarea
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder="Any additional notes about this vehicle..."
            className="w-full px-4 py-3 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
          />
        </div>
      </div>
    </div>
  );
};

// Step 3: Review & Confirm
const Step3Review = ({ vin, vinData, formData, departments, drivers }) => {
  const department = departments?.find(d => d.id === formData.department_id);
  const driver = drivers?.find(d => d.id === formData.assigned_driver_id);

  return (
    <div className="max-w-xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-success-bg rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-xl font-semibold text-ink-soft">Review & Confirm</h2>
        <p className="mt-2 text-ink-muted">
          Verify the details before adding this vehicle to your fleet
        </p>
      </div>

      <div className="bg-surface-page rounded-xl p-6 space-y-6">
        {/* Vehicle Info */}
        <div>
          <h3 className="text-sm font-medium text-ink-muted uppercase tracking-wide mb-3">
            Vehicle Information
          </h3>
          <div className="bg-surface-card rounded-lg p-4 border border-border-default">
            <div className="flex items-center">
              <div className="flex-shrink-0 h-12 w-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-accent" />
              </div>
              <div className="ml-4">
                <p className="text-lg font-medium text-ink-soft">
                  {vinData?.year} {vinData?.make} {vinData?.model}
                </p>
                <p className="text-sm text-ink-muted font-mono">{vin}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Fleet Details */}
        <div>
          <h3 className="text-sm font-medium text-ink-muted uppercase tracking-wide mb-3">
            Fleet Details
          </h3>
          <div className="bg-surface-card rounded-lg p-4 border border-border-default space-y-3">
            <div className="flex justify-between">
              <span className="text-ink-muted">Unit Number</span>
              <span className="font-medium">{formData.unit_number || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-muted">License Plate</span>
              <span className="font-medium">
                {formData.license_plate 
                  ? `${formData.license_plate} (${formData.license_plate_state_province})`
                  : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-muted">Department</span>
              <span className="font-medium">{department?.name || 'Unassigned'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-muted">Assigned Driver</span>
              <span className="font-medium">
                {driver ? `${driver.first_name} ${driver.last_name}` : 'Unassigned'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-muted">Current Odometer</span>
              <span className="font-medium">
                {formData.current_odometer 
                  ? `${formData.current_odometer.toLocaleString()} ${formData.odometer_unit}`
                  : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-ink-muted">Fuel Type</span>
              <span className="font-medium capitalize">{formData.fuel_type?.replace('_', ' ') || '-'}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {formData.notes && (
          <div>
            <h3 className="text-sm font-medium text-ink-muted uppercase tracking-wide mb-3">
              Notes
            </h3>
            <div className="bg-surface-card rounded-lg p-4 border border-border-default">
              <p className="text-ink-soft">{formData.notes}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Wizard Component
const AddVehicleWizard = () => {
  const { fleetId } = useParams();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [vin, setVin] = useState('');
  const [vinData, setVinData] = useState(null);
  const [formData, setFormData] = useState({
    unit_number: '',
    license_plate: '',
    license_plate_state_province: 'ON',
    department_id: null,
    assigned_driver_id: null,
    current_odometer: null,
    odometer_unit: 'km',
    acquisition_date: null,
    fuel_type: null,
    notes: '',
  });

  const steps = [
    { id: 'vin', title: 'Enter VIN' },
    { id: 'details', title: 'Fleet Details' },
    { id: 'review', title: 'Review' },
  ];

  // Fetch departments and drivers
  const { data: departments } = useQuery({
    queryKey: ['fleetDepartments', fleetId],
    queryFn: () => fleetApi.listDepartments(fleetId),
  });

  const { data: drivers } = useQuery({
    queryKey: ['fleetDrivers', fleetId],
    queryFn: () => fleetApi.listDrivers(fleetId),
  });

  // VIN decode mutation
  const decodeMutation = useMutation({
    mutationFn: (vinStr) => fleetApi.decodeVin(vinStr),
    onSuccess: (data) => {
      setVinData(data);
    },
  });

  // Add vehicle mutation
  const addVehicleMutation = useMutation({
    mutationFn: (data) => fleetApi.addVehicle(fleetId, data),
    onSuccess: (vehicle) => {
      navigate(`/fleet/${fleetId}/vehicles/${vehicle.id}`, {
        state: { message: 'Vehicle added successfully!' }
      });
    },
  });

  const handleNext = () => {
    if (currentStep === 0 && !vinData) {
      decodeMutation.mutate(vin);
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    addVehicleMutation.mutate({
      vin,
      ...formData,
    });
  };

  const canProceed = () => {
    if (currentStep === 0) return vinData !== null;
    return true;
  };

  return (
    <div className="min-h-screen bg-surface-page">
      {/* Header */}
      <div className="bg-surface-card border-b border-border-default">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-ink-muted hover:text-ink-soft mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Fleet
          </button>
          <h1 className="text-2xl font-bold text-ink-soft">Add Vehicle to Fleet</h1>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <StepIndicator steps={steps} currentStep={currentStep} />

        {/* Error Display */}
        {(decodeMutation.error || addVehicleMutation.error) && (
          <div className="mb-6 p-4 bg-danger-bg border border-danger rounded-lg flex items-start">
            <AlertCircle className="w-5 h-5 text-danger mt-0.5" />
            <div className="ml-3">
              <p className="text-sm font-medium text-danger">Error</p>
              <p className="text-sm text-danger">
                {decodeMutation.error?.message || addVehicleMutation.error?.message}
              </p>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-surface-card rounded-xl border border-border-default p-8">
          {currentStep === 0 && (
            <Step1VIN
              vin={vin}
              setVin={setVin}
              vinData={vinData}
              setVinData={setVinData}
              isDecoding={decodeMutation.isPending}
              onDecode={() => decodeMutation.mutate(vin)}
            />
          )}
          {currentStep === 1 && (
            <Step2Details
              formData={formData}
              setFormData={setFormData}
              departments={departments}
              drivers={drivers}
            />
          )}
          {currentStep === 2 && (
            <Step3Review
              vin={vin}
              vinData={vinData}
              formData={formData}
              departments={departments}
              drivers={drivers}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="px-6 py-3 border border-border-default rounded-lg font-medium text-ink-soft hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>

          {currentStep === steps.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={addVehicleMutation.isPending}
              className="px-6 py-3 bg-success text-white rounded-lg font-medium hover:bg-success/80 disabled:opacity-50 flex items-center"
            >
              {addVehicleMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding Vehicle...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Add Vehicle
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed() || decodeMutation.isPending}
              className="px-6 py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent-hover disabled:opacity-50 flex items-center"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddVehicleWizard;
